import { refundApi } from '../refundApi';
import { DocumentView } from '../types/documentTypes';

const documentEndpoints = refundApi.injectEndpoints({
  endpoints: (builder) => ({
    // List documents for a refund
    // GET /refunds/{company}/{center}/{refundId}/documents
    listDocuments: builder.query<DocumentView[], { company: string; center: string; refundId: string }>({
      query: ({ company, center, refundId }) => ({
        url: `refunds/${company}/${center}/${refundId}/documents`,
        method: 'GET',
      }),
      providesTags: (result, error, { company, center, refundId }) => [
        { type: 'Document', id: `${company}-${center}-${refundId}` },
        'Document',
      ],
    }),

    // Upload document to a refund
    // POST /refunds/{company}/{center}/{refundId}/documents
    uploadDocument: builder.mutation<
      DocumentView,
      { company: string; center: string; refundId: string; file: File; documentType: string }
    >({
      query: ({ company, center, refundId, file, documentType }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('documentType', documentType);

        return {
          url: `refunds/${company}/${center}/${refundId}/documents`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { company, center, refundId }) => [
        { type: 'Document', id: `${company}-${center}-${refundId}` },
        'Document',
      ],
    }),

    // Download document
    // GET /refunds/{company}/{center}/{refundId}/documents/{documentId}/download
    downloadDocument: builder.mutation<
      Blob,
      { company: string; center: string; refundId: string; documentId: string }
    >({
      query: ({ company, center, refundId, documentId }) => ({
        url: `refunds/${company}/${center}/${refundId}/documents/${documentId}/download`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // View document (get document for viewing)
    // GET /refunds/{company}/{center}/{refundId}/documents/{documentId}/view
    viewDocument: builder.query<
      Blob,
      { company: string; center: string; refundId: string; documentId: string }
    >({
      query: ({ company, center, refundId, documentId }) => ({
        url: `refunds/${company}/${center}/${refundId}/documents/${documentId}/view`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // Delete document
    // DELETE /refunds/{company}/{center}/{refundId}/documents/{documentId}
    deleteDocument: builder.mutation<
      void,
      { company: string; center: string; refundId: string; documentId: string }
    >({
      query: ({ company, center, refundId, documentId }) => ({
        url: `refunds/${company}/${center}/${refundId}/documents/${documentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { company, center, refundId }) => [
        { type: 'Document', id: `${company}-${center}-${refundId}` },
        'Document',
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListDocumentsQuery,
  useUploadDocumentMutation,
  useDownloadDocumentMutation,
  useViewDocumentQuery,
  useDeleteDocumentMutation,
} = documentEndpoints;
